import React, { useState, useEffect } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal, Eyebrow, PlaceholderBox } from "./primitives";
import { FAQS } from "../../lib/siteData";
import { Plus, Minus, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Symptoms = () => {
  const [open, setOpen] = useState(0);
  return (
    <section id="symptoms" className="bg-[#E7ECEA] pt-12 md:pt-16 pb-24 md:pb-32" data-testid="symptoms-section">
      <div className="max-w-[1240px] mx-auto px-5 md:px-8 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-16">
        <Reveal>
          <Eyebrow>02 — Patient education</Eyebrow>
          <h2 className="font-display font-light text-4xl md:text-5xl text-[#161C22] mt-5 leading-tight">
            Understanding your symptoms
          </h2>
          <p className="text-[#161C22]/70 mt-5 leading-relaxed">
            Straight answers to the questions most people arrive with — written to
            reassure, not to alarm, and never to push toward surgery.
          </p>
        </Reveal>

        <div className="divide-y divide-[#DCE3E0]" data-testid="faq-accordion">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="py-2">
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`faq-toggle-${i}`}
                  className="w-full flex items-start gap-4 text-left py-5 group"
                  aria-expanded={isOpen}
                >
                  <span className={`faq-marker ${isOpen ? "!bg-[#1F3864] !text-white" : ""}`}>
                    {isOpen ? <Minus size={15} /> : <Plus size={15} />}
                  </span>
                  <span className="font-display text-lg md:text-xl text-[#161C22] flex-1 group-hover:text-[#1F3864] transition-colors">
                    {f.q}
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="text-[#161C22]/70 leading-relaxed pl-10 pr-2 pb-6 text-[15px]">{f.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const About = () => {
  const [hospitals, setHospitals] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [aboutText, setAboutText] = useState("");

  useEffect(() => {
    axios.get(`${API}/hospitals`).then((res) => {
      setHospitals(res.data);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  useEffect(() => {
    axios.get(`${API}/about-content`).then((res) => {
      setAboutText(res.data?.text || "");
    }).catch(() => {});
  }, []);

  const showHospitals = loaded && hospitals.length > 0;

  return (
    <section id="about" className="bg-[#F3F5F4] pt-12 md:pt-16 pb-24 md:pb-32" data-testid="about-section">
      <div className="max-w-[1240px] mx-auto px-5 md:px-8 grid lg:grid-cols-[0.82fr_1.18fr] gap-12 lg:gap-16 items-start">
        <Reveal>
          <div className="bg-[#14263D] rounded-3xl p-8">
            <div className="max-w-[280px] mx-auto">
              <PlaceholderBox
                onDark
                ratio="aspect-[4/5]"
                label="Dr. Chanduka — professional portrait, non-clinical (4:5)"
              />
            </div>
            <div className="mt-7 text-center">
              <h3 className="font-display text-2xl text-white">Dr. Amit Chanduka</h3>
              <p className="text-[#5ECCA0] text-sm mt-1">Neurosurgeon &amp; Spine Surgeon</p>
              <div className="mt-5 pt-5 border-t border-white/10 font-mono-ui text-[0.78rem] uppercase tracking-wider text-[#F3F5F4]/85 space-y-2 text-left">
                <div>· MBBS</div>
                <div>· MS, M.Ch Neurosurgery (Gold Medalist)</div>
                <div>· Senior Consultant Neurosurgeon — Brain & Spine Surgery</div>
              </div>
              <div className="mt-5 pt-5 border-t border-white/10 text-[0.85rem] text-[#F3F5F4]/85 space-y-3 text-left leading-snug">
                <div>· Fellowship in Spine Surgery<br /><span className="pl-3 text-[#F3F5F4]/65">(Sir GangaRam Hospital, New Delhi, India)</span></div>
                <div>· Fellowship in Endoscopic Spine Surgery<br /><span className="pl-3 text-[#F3F5F4]/65">(Seoul, South Korea)</span></div>
                <div>· Fellowship in Endovascular Neurointervention<br /><span className="pl-3 text-[#F3F5F4]/65">(SMS Hospital, Jaipur, India)</span></div>
                <div>· Fellowship in Stereotactic & Functional Neurosurgery<br /><span className="pl-3 text-[#F3F5F4]/65">(Seoul, South Korea)</span></div>
                <div>· Formerly at Manipal Hospital & Sakra World Hospital, Bangalore</div>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <Eyebrow>03 — About</Eyebrow>
          <h2 className="font-display font-light text-4xl md:text-5xl text-[#161C22] mt-5 leading-tight">
            A calm, considered approach to care
          </h2>
          <div className="mt-7 space-y-5 text-[#161C22]/75 leading-relaxed text-base md:text-lg">
            {(aboutText || `Over fifteen years I have come to believe that good neurosurgery begins with good conversation. Before scans and options, people want to understand what is happening in their own words — so that is where every consultation starts.\n\nMy focus is minimally invasive spine surgery alongside general and brain neurosurgery. Where a smaller, gentler technique can achieve the same result, I will always favour it — and where an operation is not needed, I will say so plainly.\n\nThe aim is simple: honest options, clear explanations and a treatment plan you feel genuinely part of. You should never leave a consultation unsure of what comes next, or why.`)
              .split("\n\n")
              .map((para, i) => <p key={i}>{para}</p>)}
          
          </div>

          {showHospitals && (
            <div className="mt-10 pt-8 border-t border-[#DCE3E0]" data-testid="hospital-affiliations">
              <span className="font-mono-ui text-[0.62rem] uppercase tracking-widest text-[#3E7B5E] block mb-5">
                Hospital Affiliations
              </span>
              <div className="grid sm:grid-cols-2 gap-4">
                {hospitals.map((h, i) => (
                  <div
                    key={h.id}
                    data-testid={`hospital-card-${i}`}
                    className="flex items-center gap-4 rounded-xl border border-[#DCE3E0] bg-white p-4"
                  >
                    {h.logo_base64 ? (
                      <img src={h.logo_base64} alt={h.name} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <span className="w-12 h-12 rounded-lg bg-[#E7ECEA] text-[#1F3864] flex items-center justify-center flex-shrink-0">
                        <Building2 size={20} />
                      </span>
                    )}
                    <div>
                      <div className="font-display text-base text-[#161C22]">{h.name}</div>
                      {(h.role || h.years) && (
                        <div className="text-sm text-[#161C22]/60 mt-0.5">
                          {h.role}{h.role && h.years ? " · " : ""}{h.years}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Reveal>
      </div>
    </section>
  );
};
